import { getDashboardData, type DashboardData } from '@/lib/api/dashboard/dashboard-api'
import { getPacks, type Pack, type PackType } from '@/lib/api/packs/packs-api'
import { buyPack } from '@/lib/api/subscription-billing/subscription-billing-api'
import {
    getAiCreditBalance,
    hasCreditBalanceToShow,
    type AiCreditBalance,
} from '@/lib/api/core/ai-credits-api'
import { AiCreditBalanceSummary } from '@/components/ai-credits/ai-credit-balance-summary'
import { getVideoCredits, type VideoCreditBalance } from '@/lib/api/video/video-api'
import { withCheckoutReturn } from '@/lib/billing/checkout-return-url'
import { useResourceVisibility } from '@/lib/hooks/use-entity-access'
import { isIndividualCompany } from '@/lib/person-type'
import { useAuth } from '@/store/hooks'
import { useEffect, useState, type ReactNode } from 'react'
import { MdArrowForward, MdCloud, MdStorefront } from 'react-icons/md'
import { Link, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { PackProductCard } from './pack-product-card'
import { PACK_PRODUCTS, type PackProduct } from './store-products'

const integer = new Intl.NumberFormat('pt-BR')

type Quota = { current: number; max: number | 'unlimited' }

/** Consumo do mês de cada produto com cota no plano. Sem cota, não há linha. */
function quotaFor(type: PackType, data: DashboardData | null): Quota | null {
    if (!data) return null
    if (type === 'fiscal-notes' && data.fiscalNotes)
        return { current: data.fiscalNotes.currentCount, max: data.fiscalNotes.maxNotes }
    return null
}

/**
 * Loja do Sistema: tudo que a conta compra além do plano. Veio da aba
 * Assinatura de Minha Conta, que ficou só com o consumo e os links para cá.
 *
 * A compra gera uma cobrança e leva ao checkout público dela (`/pay/:token`),
 * o mesmo caminho de antes; o recurso só é creditado quando o pagamento entra.
 * Só o dono da conta (COMPANY) compra.
 */
export default function SystemStorePage() {
    const { user, isCompany } = useAuth()
    const isResourceVisible = useResourceVisibility()
    const [searchParams] = useSearchParams()
    const highlighted = searchParams.get('product')

    const [packs, setPacks] = useState<Pack[]>([])
    const [dashboard, setDashboard] = useState<DashboardData | null>(null)
    const [creditBalance, setCreditBalance] = useState<AiCreditBalance | null>(null)
    const [videoBalance, setVideoBalance] = useState<VideoCreditBalance | null>(null)
    const [loading, setLoading] = useState(true)
    const [buying, setBuying] = useState(false)

    // Pessoa física não emite nota fiscal: vender nota extra seria cobrar por nada.
    const isCompanyIndividual = isIndividualCompany(user?.company)

    useEffect(() => {
        if (!isCompany) return
        void (async () => {
            const [packsResponse, dashboardResponse, balanceResponse] = await Promise.all([
                getPacks(),
                getDashboardData(),
                getAiCreditBalance(),
            ])
            if (packsResponse.success) setPacks(packsResponse.result)
            else toast.error(packsResponse.error || 'Não foi possível carregar os produtos')
            if (dashboardResponse.success) setDashboard(dashboardResponse.result)
            if (balanceResponse.success) setCreditBalance(balanceResponse.result)
            // Só quem tem vídeo no plano recebe o saldo; os demais não veem o produto.
            const videoResponse = await getVideoCredits()
            if (videoResponse.success && videoResponse.result) setVideoBalance(videoResponse.result)
            setLoading(false)
        })()
    }, [isCompany])

    // O link vindo de um card de consumo abre direto no produto.
    useEffect(() => {
        if (loading || !highlighted) return
        document.getElementById(`product-${highlighted}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, [loading, highlighted])

    const isOffered = (product: PackProduct): boolean => {
        if (product.type === 'fiscal-notes' && isCompanyIndividual) return false
        // Créditos de vídeo: só para quem tem videoconferência no plano e com a cobrança ligada.
        if (product.type === 'video-credits') return Boolean(videoBalance?.enabled)
        // Cota ilimitada no plano: não há o que comprar.
        const quota = quotaFor(product.type, dashboard)
        if (product.type !== 'ai-credits' && (!quota || quota.max === 'unlimited')) return false
        return true
    }

    const buy = async (input: { code: string } | { type: PackType; quantity: number }) => {
        setBuying(true)
        try {
            const response = await buyPack(input)
            if (!response.success || !response.result) {
                toast.error(response.error || 'Não foi possível gerar a cobrança')
                return
            }
            if (!response.result.payUrl) {
                toast.error('Cobrança gerada, mas sem link de pagamento. Consulte o financeiro.')
                return
            }
            // `return_url` + `from=app`: o checkout roda fora do layout e precisa da volta.
            window.location.href = withCheckoutReturn(response.result.payUrl, 'app')
        } catch {
            toast.error('Não foi possível gerar a cobrança')
        } finally {
            setBuying(false)
        }
    }

    const usageLine = (type: PackType): ReactNode => {
        if (type === 'ai-credits') {
            return hasCreditBalanceToShow(creditBalance) ? (
                <div className="mt-2 rounded-box bg-base-200/60 p-4">
                    <AiCreditBalanceSummary balance={creditBalance} />
                </div>
            ) : null
        }
        if (type === 'video-credits') {
            if (!videoBalance) return null
            return (
                <span className="text-base-content/70">
                    Saldo: <span className={videoBalance.totalCredits <= 0 ? 'text-error font-medium' : 'font-medium'}>
                        {integer.format(Math.floor(videoBalance.totalCredits))} minutos
                    </span>
                    {videoBalance.plan && ` (plano: ${integer.format(Math.floor(videoBalance.plan.remainingCredits))} de ${integer.format(videoBalance.plan.grantedCredits)} no mês)`}
                </span>
            )
        }
        const quota = quotaFor(type, dashboard)
        if (!quota || quota.max === 'unlimited') return null
        const available = Math.max(0, quota.max - quota.current)
        return (
            <span className="text-base-content/70">
                Este mês: {integer.format(quota.current)} de {integer.format(quota.max)} usados,{' '}
                <span className={available === 0 ? 'text-error font-medium' : 'font-medium'}>
                    {integer.format(available)} disponíveis
                </span>
            </span>
        )
    }

    if (!isCompany) {
        return (
            <div className="p-4 sm:p-6">
                <div className="card bg-base-100 border border-base-300">
                    <div className="card-body items-center text-center py-14">
                        <MdStorefront className="text-4xl text-base-content/30" />
                        <p className="font-medium">A Loja do Sistema é do titular da conta</p>
                        <p className="text-sm text-base-content/60">
                            Peça ao responsável pela conta para comprar o que precisar.
                        </p>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="p-4 sm:p-6 space-y-6 max-w-5xl">
            <div>
                <h1 className="text-2xl font-semibold flex items-center gap-2">
                    <MdStorefront className="text-primary" />
                    Loja do Sistema
                </h1>
                <p className="text-sm text-base-content/60 mt-0.5">
                    Créditos e recursos extras para a sua conta. A compra gera uma cobrança, e o
                    recurso é liberado assim que o pagamento é confirmado.
                </p>
            </div>

            {loading ? (
                <div className="flex justify-center py-20">
                    <span className="loading loading-spinner loading-lg text-primary" />
                </div>
            ) : (
                <>
                    {PACK_PRODUCTS.filter(isOffered).map(product => (
                        <PackProductCard
                            key={product.type}
                            product={product}
                            packs={packs.filter(pack => pack.type === product.type)}
                            usage={usageLine(product.type)}
                            highlighted={highlighted === product.type}
                            buying={buying}
                            unavailableReason={
                                creditBalance?.platformAccount
                                    ? 'A conta da plataforma não compra pacotes: o consumo dela é custo da própria plataforma.'
                                    : undefined
                            }
                            onBuyPack={code => void buy({ code })}
                            onBuyCustom={quantity => void buy({ type: product.type, quantity })}
                        />
                    ))}

                    {/* Armazenamento tem fluxo próprio (GB por mês, recorrente), na
                        tela de Armazenamento. Aqui só o caminho até ela. */}
                    {isResourceVisible('storage', 'settings') && (
                        <section className="card bg-base-100 border border-base-300">
                            <div className="card-body p-5 sm:p-6">
                                <div className="flex flex-wrap items-center gap-3">
                                    <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                                        <MdCloud className="text-xl" />
                                    </div>
                                    <div className="flex-1 min-w-48">
                                        <h2 className="text-lg font-semibold">Armazenamento extra</h2>
                                        <p className="text-sm text-base-content/60">
                                            GB adicionais por mês para os arquivos da organização.
                                        </p>
                                    </div>
                                    <Link to="/storage/settings" className="btn btn-outline btn-primary btn-sm">
                                        Contratar espaço
                                        <MdArrowForward />
                                    </Link>
                                </div>
                            </div>
                        </section>
                    )}
                </>
            )}
        </div>
    )
}
